/**
 * Comportement clavier d'une fenêtre modale (dialogue, panneau latéral).
 *
 * Échap ferme, et le focus entre dans la fenêtre à l'ouverture puis revient à
 * l'élément d'origine à la fermeture : sans ça, le clavier et le lecteur d'écran
 * restaient sur la page masquée derrière le voile. La tabulation boucle dans la
 * fenêtre : `aria-modal` suffit au lecteur d'écran, pas au clavier.
 *
 * Fenêtres empilées (un dialogue ouvert depuis un panneau) : seule celle qui
 * contient le focus réagit, sinon Échap les fermerait toutes d'un coup.
 */
import { useEffect, type RefObject } from "react";

const FOCUSABLES =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useFenetreModale(
  ouverte: boolean,
  panneau: RefObject<HTMLElement | null>,
  /** Ref plutôt que fonction : un `onClose` recréé à chaque rendu relancerait
   *  l'effet, et rendrait le focus à l'origine à chaque frappe. */
  fermer: RefObject<() => void>,
) {
  useEffect(() => {
    if (!ouverte) return;
    const origine = document.activeElement as HTMLElement | null;
    panneau.current?.focus();
    const surTouche = (e: KeyboardEvent) => {
      const fenetre = panneau.current;
      if (!fenetre || !fenetre.contains(document.activeElement)) return;
      if (e.key === "Escape") {
        fermer.current();
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = fenetre.querySelectorAll<HTMLElement>(FOCUSABLES);
      const premier = focusables[0];
      const dernier = focusables[focusables.length - 1];
      if (!premier || !dernier) return;
      if (e.shiftKey && document.activeElement === premier) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && document.activeElement === dernier) {
        e.preventDefault();
        premier.focus();
      }
    };
    window.addEventListener("keydown", surTouche);
    return () => {
      window.removeEventListener("keydown", surTouche);
      origine?.focus();
    };
  }, [ouverte, panneau, fermer]);
}
